import { type ReactElement, useEffect, useState } from 'react';
import { getToastMessage } from '@/shared/copy/toasts';
import { s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { Button } from '@/ui/primitives/Button';
import { Switch } from '@/ui/primitives/Switch';
import type { useUpdateProject } from '@/features/projects';
import type { ProjectDto } from '@flowqa/shared';

type MutationErrorController = { clearError: () => void; setError: (err: unknown) => void };

type NotifyInput = { message: string; tone: 'green' | 'blue' | 'red' };

type TestConfigurationSwitchRowProps = {
  checked: boolean;
  description: string;
  disabled: boolean;
  label: string;
  onCheckedChange: (checked: boolean) => void;
};

type TestConfigurationControlGroupProps = {
  children: ReactElement | ReactElement[];
  title: string;
};

const testConfigurationControlGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'minmax(0, 1fr) auto',
  },
  columnGap: s('md'),
  rowGap: s('sm'),
  alignItems: 'center',
} as const;

const controlDescriptionTextSx = {
  maxWidth: '62ch',
} as const;

const shouldPreferFileNameOverDisplayName = (project: ProjectDto | null): boolean =>
  project?.testDataControls.preferFileNameOverDisplayName ?? false;

const shouldUseDescribeBlockAsSuiteName = (project: ProjectDto | null): boolean =>
  !shouldPreferFileNameOverDisplayName(project);

const TestConfigurationSwitchRow = ({
  checked,
  description,
  disabled,
  label,
  onCheckedChange,
}: TestConfigurationSwitchRowProps): ReactElement => (
  <Box sx={testConfigurationControlGridSx}>
    <Stack gap="xxs">
      <Text role="body">{label}</Text>
      <Text role="bodyMuted" sx={controlDescriptionTextSx}>
        {description}
      </Text>
    </Stack>
    <Switch
      size="small"
      density="compact"
      checked={checked}
      disabled={disabled}
      onChange={(_event, nextChecked) => {
        onCheckedChange(nextChecked);
      }}
    />
  </Box>
);

const TestConfigurationControlGroup = ({
  children,
  title,
}: TestConfigurationControlGroupProps): ReactElement => (
  <Stack gap="md">
    <Text role="bodyStrong" component="h3">
      {title}
    </Text>
    <Stack gap="md">{children}</Stack>
  </Stack>
);

export type ProjectTestDataControlsSectionProps = {
  project: ProjectDto | null;
  updateProject: ReturnType<typeof useUpdateProject>;
  mutationError: MutationErrorController;
  notify: (input: NotifyInput) => void;
};

export const ProjectTestDataControlsSection = ({
  project,
  updateProject,
  mutationError,
  notify,
}: ProjectTestDataControlsSectionProps): ReactElement => {
  const [retainDiagnosticOutput, setRetainDiagnosticOutput] = useState(
    () => project?.testDataControls.retainDiagnosticOutput ?? false,
  );
  const [includeResultsFileMetadata, setIncludeResultsFileMetadata] = useState(
    () => !(project?.testDataControls.strictDataMinimisation ?? true),
  );
  const [groupErroredWithFailed, setGroupErroredWithFailed] = useState(
    () => project?.testDataControls.groupErroredWithFailed ?? true,
  );
  const [useDescribeBlockAsSuiteName, setUseDescribeBlockAsSuiteName] = useState(() =>
    shouldUseDescribeBlockAsSuiteName(project),
  );

  useEffect(() => {
    if (!project) {
      return;
    }
    setRetainDiagnosticOutput(project.testDataControls.retainDiagnosticOutput);
    setIncludeResultsFileMetadata(!project.testDataControls.strictDataMinimisation);
    setGroupErroredWithFailed(project.testDataControls.groupErroredWithFailed);
    setUseDescribeBlockAsSuiteName(shouldUseDescribeBlockAsSuiteName(project));
  }, [project]);

  const isDirty =
    project !== null &&
    (retainDiagnosticOutput !== project.testDataControls.retainDiagnosticOutput ||
      includeResultsFileMetadata !== !project.testDataControls.strictDataMinimisation ||
      groupErroredWithFailed !== project.testDataControls.groupErroredWithFailed ||
      useDescribeBlockAsSuiteName !== shouldUseDescribeBlockAsSuiteName(project));

  return (
    <Panel title="Test Configuration" surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
      <Stack gap="lg">
        <Stack gap="xl">
          <TestConfigurationControlGroup title="Display Settings">
            <TestConfigurationSwitchRow
              label="Count Errored Status As Failed"
              description="Group errored runs with failures in summaries, thresholds, and breakdowns. This will hide error-related status components."
              checked={groupErroredWithFailed}
              disabled={!project || updateProject.isPending}
              onCheckedChange={setGroupErroredWithFailed}
            />
            <TestConfigurationSwitchRow
              label="Use Describe Block As Suite Name"
              description="For JavaScript and TypeScript testing frameworks, use the top-level describe(...) label as suite name instead of file name where avaialble."
              checked={useDescribeBlockAsSuiteName}
              disabled={!project || updateProject.isPending}
              onCheckedChange={setUseDescribeBlockAsSuiteName}
            />
          </TestConfigurationControlGroup>
          <TestConfigurationControlGroup title="Upload Settings">
            <TestConfigurationSwitchRow
              label="Preserve Results File Output"
              description="Store parsed stdout, stderr, and failure output from automated results files."
              checked={retainDiagnosticOutput}
              disabled={!project || updateProject.isPending}
              onCheckedChange={setRetainDiagnosticOutput}
            />
            <TestConfigurationSwitchRow
              label="Include Results File Metadata"
              description="Store additional metadata parsed from uploaded results files."
              checked={includeResultsFileMetadata}
              disabled={!project || updateProject.isPending}
              onCheckedChange={setIncludeResultsFileMetadata}
            />
          </TestConfigurationControlGroup>
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: s('sm') }}>
          <Button
            variant="ghost"
            fullWidthOnMobile={false}
            disabled={!isDirty || updateProject.isPending}
            onClick={() => {
              if (!project) {
                return;
              }
              setRetainDiagnosticOutput(project.testDataControls.retainDiagnosticOutput);
              setIncludeResultsFileMetadata(!project.testDataControls.strictDataMinimisation);
              setGroupErroredWithFailed(project.testDataControls.groupErroredWithFailed);
              setUseDescribeBlockAsSuiteName(shouldUseDescribeBlockAsSuiteName(project));
              mutationError.clearError();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidthOnMobile={false}
            disabled={!isDirty || updateProject.isPending}
            loading={updateProject.isPending}
            onClick={() => {
              if (!project) {
                return;
              }
              updateProject.mutate(
                {
                  id: project.id,
                  testDataControls: {
                    retainDiagnosticOutput,
                    strictDataMinimisation: !includeResultsFileMetadata,
                    groupErroredWithFailed,
                    preferFileNameOverDisplayName: !useDescribeBlockAsSuiteName,
                  },
                },
                {
                  onSuccess: () => {
                    mutationError.clearError();
                    notify({
                      message: getToastMessage('project.testConfigurationUpdated'),
                      tone: 'green',
                    });
                  },
                  onError: (error) => {
                    mutationError.setError(error);
                  },
                },
              );
            }}
          >
            Update
          </Button>
        </Box>
      </Stack>
    </Panel>
  );
};
